import { useCallback, useEffect, useMemo, useState } from "react";

import { Button, toastError, toastSuccess } from "@alpacahq/ui/old";

import { FullScreenSpinner } from "~/v2/components/common";
import { useGetAccessKeys } from "../../../../api/hooks";
import type { Product } from "../../../../api/rest/account";
import {
  createNewAccessKey,
  deleteAccessKey,
} from "../../../../api/rest/authx";

type APIKeysProps = {
  accountID?: string;
  product?: Product;
};

export const APIKeys = ({ product, accountID }: APIKeysProps) => {
  // set up component state using useState hook
  const [secret, setSecret] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [isGenerating, setGenerating] = useState(false);
  const [canGenerate, setCanGenerate] = useState(false);

  const endpoint =
    // determine API endpoint based on whether this is a paper account
    product === "paper"
      ? window.env.PAPER_API_ENDPOINT
      : window.env.API_ENDPOINT;

  // retrieve access keys using useGetAccessKeys hook
  const { access_keys, refetch, isFetching } = useGetAccessKeys({
    accountID,
    product,
    options: {
      enabled: !!accountID,
    },
  });

  // v5 removed query onSettled; clear the generating flag once the fetch settles.
  useEffect(() => {
    if (!isFetching) {
      setGenerating(false);
    }
  }, [isFetching]);

  // retrieve active access key ID using useMemo hook
  const accessKeyID = useMemo(() => {
    // search for active key whose account ID matches and return its ID
    const activeKeyID = access_keys.find(
      (key) =>
        key.status?.toUpperCase() === "ACTIVE" && key.account_id === accountID,
    )?.id;

    // if an active key exists, return its ID
    if (activeKeyID) {
      return activeKeyID;
    }

    // otherwise, default to the ID of the first key
    return access_keys?.[0]?.id;
  }, [access_keys, accountID]);

  // define function to generate a new access key
  const generateKey = useCallback(async () => {
    try {
      // indicate that a key is being generated
      setGenerating(true);

      // create a new access key using createNewAccessKey function and the current account ID
      const { secret } = await createNewAccessKey({
        accountID,
        product,
      });

      // set the secret state to the generated key
      setSecret(secret);

      // show success toast notification
      toastSuccess("New key generated");
    } catch {
      // show error toast notification if there was an error generating the key
      toastError("Failed to generate new key");
    } finally {
      // refresh access key data
      refetch();
    }
  }, [accountID, product, refetch]);

  // define function to regenerate the current access key
  const regenerateKey = useCallback(async () => {
    try {
      // delete all access keys, which will also delete the active key
      await Promise.all(
        access_keys.map((key) =>
          deleteAccessKey({
            accessKeyID: key.id,
            accountID,
            product,
          }),
        ),
      ).catch(console.error); // TODO: handle errors! we should not just hide active client credentials if this fails
      // generate a new access key
      await generateKey();
    } catch {
      // show error toast notification if there was an error regenerating the key
      toastError("Failed to regenerate key");
    }
  }, [access_keys, accountID, product, generateKey]);

  // set timeout to enable key regeneration after 3 seconds
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCanGenerate(true);
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  // if access key data is undefined, display a Spinner component
  if (access_keys === undefined) {
    return <FullScreenSpinner />;
  }

  // otherwise, render the Keys component
  return (
    <div>
      <div className="mb-4">
        <label className="mb-2 block font-medium">Endpoint</label>
        <input
          className="block w-full rounded-md border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-none"
          type="text"
          value={endpoint}
          readOnly
        />
      </div>
      {!!accessKeyID && (
        <>
          <div className="mb-4">
            <label className="mb-2 block font-medium">Key</label>
            <input
              className="block w-full rounded-md border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-none"
              type="text"
              value={accessKeyID || "no key found"}
              disabled={isGenerating}
              readOnly
            />
          </div>
          {!!secret && !isGenerating && (
            <>
              <div className="mb-4">
                <label className="mb-2 block font-medium">Secret</label>
                <input
                  className="block w-full rounded-md border-gray-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-none"
                  type="text"
                  value={secret}
                  readOnly
                />
              </div>
              <p className="mb-4 text-xs italic">
                Note: Your secret key will disappear after refreshing or
                navigating away from this page.
              </p>
            </>
          )}{" "}
          {confirm ? (
            // if confirm is true, display confirmation message and buttons
            <>
              <p className="mb-4 text-xs italic">
                Note: When a new key is generated, your current key will be
                invalidated and any programs using the current key will stop
                working.
              </p>
              <div className="flex justify-between">
                <Button
                  variant="alternate"
                  className="mr-4"
                  disabled={isGenerating}
                  onClick={() => {
                    setConfirm(false);
                    regenerateKey();
                  }}
                >
                  Generate New Keys
                </Button>
                <Button variant="alternate" onClick={() => setConfirm(false)}>
                  Cancel
                </Button>
              </div>
            </>
          ) : !secret ? (
            // if there is no secret key, display "Regenerate" button
            <Button
              variant="alternate"
              className="w-full"
              disabled={!canGenerate || isGenerating}
              onClick={() => setConfirm(true)}
            >
              Regenerate
            </Button>
          ) : (
            // otherwise, display "Hide" button
            <Button
              className="w-full"
              variant="alternate"
              onClick={() => setSecret("")}
            >
              Hide
            </Button>
          )}
        </>
      )}
      {accessKeyID === undefined && (
        // if accessKeyID is undefined, display "Generate New Keys" button
        <Button
          variant="alternate"
          className="w-full"
          disabled={!canGenerate || isGenerating}
          onClick={() => generateKey()}
        >
          Generate New Keys
        </Button>
      )}
    </div>
  );
};

export default APIKeys;
